import { useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, Scissors, Check } from "lucide-react";
import { streamUrl, formatTime, splitVideo } from "../lib/api";
import { useSegments } from "../hooks/useSegments";

function CreateButtonLabel({ done, working }) {
  if (done) {
    return (
      <>
        <Check className="h-6 w-6" /> Your videos are ready
      </>
    );
  }
  if (working) return "Working…";
  return "Create the videos";
}

export default function SplitVideo({ video, onBack, onDone }) {
  const videoRef = useRef(null);
  const durationS = (video.duration || 0) / 1000;
  const { segments, splitAt, rename, undoSplit } = useSegments(video.name, durationS);
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState(false);

  const handleSplitHere = () => {
    const t = videoRef.current ? videoRef.current.currentTime : 0;
    if (!splitAt(t)) {
      toast("Play the video to a point between the start and end, then tap Split here.");
      return;
    }
    toast.success(`Split added at ${formatTime(t)}`);
  };

  const handleCreate = async () => {
    if (segments.length < 2) {
      toast("Add at least one split first using the Split here button.");
      return;
    }
    if (segments.some((s) => !s.name.trim())) {
      toast.error("Please give every video a name.");
      return;
    }
    setWorking(true);
    const payload = segments.map((s) => ({
      name: s.name.trim(),
      start: formatTime(s.start),
      end: formatTime(s.end),
    }));
    try {
      const res = await splitVideo(video.ratingKey, payload);
      setDone(true);
      toast.success(`Done — created ${res.count} videos`);
      setTimeout(() => onDone(), 1800);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
      setWorking(false);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="glass sticky top-0 z-30 border-b border-white/5">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-3.5">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 rounded-lg py-1.5 pr-3 text-[17px] font-medium text-[#0A84FF] hover:opacity-80 transition-opacity"
            data-testid="split-back-button"
          >
            <ChevronLeft className="h-6 w-6" />
            Back
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 pb-24">
        <h1 className="mt-5 text-3xl font-bold tracking-tight text-white">
          Split “{video.name}”
        </h1>
        <p className="mt-2 mb-5 text-lg text-white/50">
          Play the video. When one memory ends and another begins, pause and tap{" "}
          <span className="font-semibold text-white">Split here</span>.
        </p>

        <video
          ref={videoRef}
          src={streamUrl(video.ratingKey)}
          controls
          playsInline
          className="w-full rounded-3xl bg-black ring-1 ring-white/10 shadow-2xl shadow-black/50"
          data-testid="split-player"
        />

        <button
          onClick={handleSplitHere}
          className="mt-5 inline-flex h-16 w-full items-center justify-center gap-2.5 rounded-2xl bg-[#0A84FF] text-2xl font-semibold text-white hover:bg-[#409CFF] transition-colors"
          data-testid="split-here-button"
        >
          <Scissors className="h-6 w-6" />
          Split here
        </button>

        <div className="mt-9">
          <p className="text-xl font-semibold text-white" data-testid="segment-count">
            This tape will become {segments.length} {segments.length === 1 ? "video" : "videos"}
          </p>
          <p className="mt-1 mb-5 text-base text-white/45">
            {segments.length === 1
              ? "Add a split above to make more than one video."
              : "Give each one a name below."}
          </p>

          <div className="flex flex-col gap-4">
            {segments.map((s, i) => (
              <div
                key={s.id}
                className="rounded-3xl bg-[#1c1c1e] p-5 ring-1 ring-white/10 animate-pop"
                data-testid="segment-card"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-lg font-semibold text-[#0A84FF]">Video {i + 1}</span>
                  {i > 0 && (
                    <button
                      onClick={() => undoSplit(s.id)}
                      className="rounded-full bg-white/10 px-4 py-1.5 text-base font-medium text-white hover:bg-white/20 transition-colors"
                      data-testid="remove-split-button"
                    >
                      Undo this split
                    </button>
                  )}
                </div>
                <p className="mb-3 text-base text-white/55">
                  From {formatTime(s.start)} to {formatTime(s.end)}
                </p>
                <input
                  type="text"
                  value={s.name}
                  onChange={(e) => rename(s.id, e.target.value)}
                  placeholder="Name this video…"
                  className="h-13 w-full rounded-2xl bg-white/[0.06] px-5 py-3 text-xl text-white placeholder-white/35 outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-[#0A84FF] transition-all"
                  data-testid="segment-name-input"
                />
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={handleCreate}
          disabled={working || segments.length < 2}
          className={`mt-9 inline-flex h-16 w-full items-center justify-center gap-2.5 rounded-2xl text-2xl font-semibold text-white transition-colors disabled:opacity-40 ${
            done ? "bg-[#30D158]" : "bg-[#0A84FF] hover:bg-[#409CFF]"
          }`}
          data-testid="create-videos-button"
        >
          <CreateButtonLabel done={done} working={working} />
        </button>
        <p className="mt-3 text-center text-base text-white/40">
          Your original tape is kept safe and is never deleted.
        </p>
      </main>
    </div>
  );
}
